import { MatchingChallengeEntity, MatchingChallengeSummaryEntity } from "./challenge";
import { KeyValueRecord } from "./common";

export interface DonationForm {
  id: number;
  title: string;
  headline: string;
  description?: string;
  default: boolean;
  kind: 'primary' | 'pledge' | 'call_center'; // pledge is deprecated
  matching_challenge?: MatchingChallengeEntity | null;
  donation_tiers: DonationTier[];
  gifts: FormGift[];
  default_monthly: boolean;
  show_email_opt_in: boolean;
  show_why_you_support_prompt: boolean;
  allow_campaign_entry: boolean;
  evergreen_upsell?: boolean;
  evergreen_cta_headline: string | null;
  evergreen_cta_description: string | null;
  campaign: {
    id: number;
    code: string;
  };
  paypal: boolean;
  /** Public SDK client ID from Core's environment configuration. */
  paypal_client_id?: string | null;
  apple_pay: boolean;
  always_show_address: boolean;
  url: string;
  preview_url: string;
  poll_id: number | null;
  status: 'draft' | 'published';
  created_at: string;
  updated_at: string;
  background_image: Asset | null;
  theme_image: Asset | null;
  theme_settings: KeyValueRecord | null;
  theme_content: KeyValueRecord | null;
  archived: boolean;
}

export type DonationFrequency = 'monthly' | 'one-time';

export interface DonationMemberDetailEntity {
  mailing_address?: DonationMemberDetailEntity['shipping_address'];
  id: number;
  email: string;
  phone_number: string;
  first_name: string;
  last_name: string;
  newsletter: boolean;
  shipping_address?: {
    country: string;
    street: string;
    city: string;
    state: string;
    postal_code: string;
  };
}

export interface DonationPaymentMethod {
  id: number;
  processor: string | null;
  account_type: 'credit_card' | 'bank_account' | 'bill_later' | 'paypal' | 'apple_pay';
  delivery_method?: string;
  last_digits?: number;
  credit_card?: {
    id: number;
    number: string;
    brand: string;
    exp_month: string;
    exp_year: string;
  };
  bank_account?: {
    id: number;
    first_name: string;
    last_name: string;
    bank_name: string;
    account_type: 'checking' | 'savings' | 'business checking';
    routing_number: string;
    account_number: string;
  };
}

/**
 * A promise: a staff-entered donation recorded with no payment. Represented as
 * the bill_later account type with a delivery_method of 'none', which is what
 * distinguishes it from legacy bill-later invoicing (email / physical).
 */
export const isPromise = (
  accountType?: string | null,
  deliveryMethod?: string | null,
): boolean => accountType === 'bill_later' && deliveryMethod === 'none';

export interface DonationTier {
  id: number;
  tier: number;
  single_donation_amount: string;
  monthly_donation_amount: string;
  flag: string | null;
}

export interface FormGift {
  id: number;
  gift_id: number;
  name: string;
  description: string;
  long_description: string;
  price_per_single: string;
  price_per_monthly: string;
  fair_market_value: string;
  stock_level: number;
  image: Asset | null;
  alternate_image: Asset | null;
  gift_variants: GiftVariant[];
  order_index: number;
}

export interface GiftVariant {
  id: number;
  default: boolean;
  name: string;
  gift_name: string;
  gift_description: string;
  gift_fair_market_value: string;
  stock_level: number;
  supplier: string;
  sku: number;
  image: Asset | null;
  alternate_image: Asset | null;
}

export interface GiftVariantHistoryEntry extends GiftVariant {
  quantity: number;
  gift_long_description: string;
  created_at: string;
  superseded_at: string | null;
}

export interface Asset {
  id: number;
  file_type: string;
  title: string;
  caption: string | null;
  cdn_url: string;
  src: string;
  thumbnail: string;
  width: number | null;
  height: number | null;
}

export interface ReceiptData {
  donationId: number;
  memberId: number;
  pollId: number | null;
  receiptId: string;
  email: string;
  amount: number;
  amountWithCoverCost: number;
  monthly: boolean;
  coverCost: boolean;
  coverCostAmount: number;
  matchingChallengeDonation: MatchingChallengeDonation | null;
  campaign: CampaignData;
  giftImageUrl: string | null;
  dtdCompany: string | null;
  formUrl: string;
  showWhyYouSupportPrompt: boolean;
}

export interface MatchingChallengeDonation {
  id: number;
  matching_challenge_id: number;
  matching_challenge_title: string;
  form_id: number;
  donation_id: number;
  multiplier?: number;
  /** Member donation amount in decimal dollars (e.g., 100.50 for $100.50), not integer cents. */
  member_amount: number;
  supporter_amount_base?: number;
  /** Supporter amount applied in decimal dollars (e.g., 100.50 for $100.50), not integer cents. */
  supporter_amount_applied?: number;
  challenge_minutes_remaining?: number;
  challenge_status?: string;
  calculated_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface CampaignData {
  id: number;
  code: string;
  group_code?: string;
}

export interface DonationDtdCompany {
  id: number;
  name: string;
}

export interface PaymentSubscriptionSummary {
  id: number;
  processor_id: string;
  processor: string;
  status: string;
  external_url: string;
  sync_message: string;
  cancel_reason_code: string;
  cancelled_by: string;
  created_at: string;
  canceled_at: string;
  last_sync_at: string;
}

export interface DonationResponse {
  id: number;
  receipt_id: string;
  form: DonationForm | null;
  amount: number;
  cover_cost_amount: number;
  monthly: boolean;
  status: string;
  bill_date: string;
  last_submitted_at: string;
  next_bill_date: string;
  email_opt_in: boolean;
  source?: string;
  payment_method: DonationPaymentMethod;
  dedicated_to?: string;
  dedicated_to_sticker?: string;
  member: DonationMemberDetailEntity;
  gift_variants: GiftVariant[];
  created_at: string;
  updated_at: string;
  first_debit: string;
  last_debit: string;
  ytd_amount?: number;
  payment_subscriptions: PaymentSubscriptionSummary[];
  sfmc_subscriber_key: string | null;
  matching_challenge_donation: MatchingChallengeDonation;
  campaign: CampaignData;
  dtd_company?: DonationDtdCompany | null;
  completion_url?: string;
}

// Donate API: /donate/v1/donations/search (swagger: Donation)
export interface DonationSearchEntity {
  id: number
  receipt_id: string
  status: string
  // Donate API uses Decimal for these fields, which may be serialized as strings.
  amount: number | string
  yearly_amount: number | string
  cover_cost_amount: number
  monthly: boolean
  email_opt_in: boolean
  created_at: string
  updated_at: string
  member_first_name: string
  member_last_name: string
  matching_challenge_id: number
  form: { id: number; title: string } | null
  campaign: { id: number; code: string }
  matching_challenge: MatchingChallengeSummaryEntity | null
  payment_method: { account_type: string | null; processor: string | null; card_type?: string | null; delivery_method?: string | null } | null
}

export interface DonationFormSearchEntity {
  id: number
  title: string
  kind: 'primary' | 'pledge' | 'call_center'
  status: 'draft' | 'published'
  default: boolean
  archived: boolean
  campaign_code: string
  url: string
  created_at: string
  allow_campaign_entry: boolean
  theme_settings: unknown
  total_amount_cents: number
  evergreen_amount_cents: number
  single_amount_cents: number
}

export interface DonateDonationsSearchRequest {
  q: object
  page: number
  per_page: number
}

export interface DoubleTheDonationCompany {
  enteredText: string;
  status: string;
  id: string;
  name: string;
}

export interface SubmissionError {
  message: string;
  code?: string;
}

export interface DonationSubmissionResult {
  success: boolean;
  receipt?: ReceiptData;
  errors: SubmissionError[];
  redirectUrl?: string;
}

export interface DonationSummary {
  count: number | null
  totalAmountCents: number | null
}

export const CANCEL_REASON_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'change_monthly_amount', label: 'Change monthly amount' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'federal_cuts', label: 'Federal Cuts' },
  { value: 'financial_reasons_cited', label: 'Financial reasons cited' },
  { value: 'giving_one_time_payments', label: 'Giving one time payments' },
  { value: 'giving_through_employer', label: 'Giving through employer' },
  { value: 'giving_through_ira_family_foundation', label: 'Giving through IRA/family foundation' },
  { value: 'lack_of_payment', label: 'Lack of Payment' },
  { value: 'meant_to_donate_for_one_year_only', label: 'Meant to donate for 1 year only' },
  { value: 'migrated_for_npr_plus_bundle', label: 'Migrated for NPR Plus Bundle' },
  { value: 'moved_away', label: 'Moved Away' },
  { value: 'unhappy_with_kuow', label: 'Unhappy with KUOW' },
  { value: 'updated_payment_information', label: 'Updated payment information' },
  { value: 'other', label: 'No reason offered' },
] as const

const cancelReasonMap = new Map<string, string>(CANCEL_REASON_OPTIONS.map((r) => [r.value, r.label]))

/**
 * Formats a cancel reason code to its display label.
 * Falls back to title-casing the code if not found in the predefined list.
 */
export function formatCancelReasonCode(code: string): string {
  const label = cancelReasonMap.get(code)
  if (label) return label
  // Fallback: convert underscore_case to Title Case
  return code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Maps API response to receipt data
 */
export function mapResponseToReceipt(response: DonationResponse): ReceiptData {
  const amount = typeof response.amount === 'string' ? parseFloat(response.amount) : response.amount;
  const coverCostAmount = typeof response.cover_cost_amount === 'string' 
    ? parseFloat(response.cover_cost_amount) 
    : response.cover_cost_amount;
  
  const baseAmount = amount - coverCostAmount;
  const giftImageUrl = response.gift_variants.length > 0 
    ? (response.gift_variants[0].alternate_image?.cdn_url ?? response.gift_variants[0].image?.cdn_url ?? null)
    : null;

  // Extract show_why_you_support_prompt from form's theme_settings
  // The form in donation response might not be normalized, so we need to handle the raw API structure
  let showWhyYouSupportPrompt = true; // Default to true
  if (response.form) {
    // Try the normalized field first (if the form was already normalized)
    if (typeof response.form.show_why_you_support_prompt === 'boolean') {
      showWhyYouSupportPrompt = response.form.show_why_you_support_prompt;
    } 
    // Check theme_settings for the setting (API stores it here)
    else if (response.form.theme_settings && typeof response.form.theme_settings === 'object') {
      const themeSettings = response.form.theme_settings;
      if (typeof themeSettings.show_why_you_support_prompt === 'boolean') {
        showWhyYouSupportPrompt = themeSettings.show_why_you_support_prompt;
      }
    }
  }

  return {
    donationId: response.id,
    memberId: response.member.id,
    pollId: response.form?.poll_id ?? null,
    receiptId: response.receipt_id,
    email: response.member.email,
    amount: baseAmount,
    amountWithCoverCost: amount,
    monthly: response.monthly,
    coverCost: coverCostAmount > 0,
    coverCostAmount: coverCostAmount,
    matchingChallengeDonation: response.matching_challenge_donation,
    campaign: response.campaign,
    giftImageUrl,
    dtdCompany: response.dtd_company?.name ?? null,
    formUrl: response.form?.url ?? '',
    showWhyYouSupportPrompt
  };
}
